import { get } from 'lodash'

import { isEmpty } from '@sigma-frontend-libs/commons'

import { isChildOf } from '@/utils'

/**
 * Обработка нажатий указателя: определение области нажатия,
 * раскрытие и закрытие списка, выбор пункта и перевод фокуса на
 * поле ввода.
 */
export const pointer = {
  computed: {
    /**
     * Отдает элемент поля ввода.
     * Если ссылка еще не установлена, отдается undefined.
     *
     * @returns {HTMLElement|undefined} Элемент поля ввода.
     */
    input() {
      return this.$refs && this.$refs.input && (this.$refs.input.$el || this.$refs.input)
    }
  },

  /**
   * После отрисовки начинается обработка нажатий по документу,
   * чтобы отслеживать нажатия вне компонента.
   */
  mounted() {
    if (document) {
      if (document.body) {
        document.body.addEventListener('click', this._clickHandler)
      }
    }
  },

  /**
   * Перед удалением компонента обработка нажатий по документу
   * прекращается.
   */
  beforeDestroy() {
    if (document) {
      if (document.body) {
        document.body.removeEventListener('click', this._clickHandler)
      }
    }
  },

  methods: {
    /**
     * Отменяет действие нажатия по умолчанию и его дальнейшее
     * распространение.
     *
     * @param {MouseEvent} event Событие нажатия.
     */
    _preventClick(event) {
      event.preventDefault()
      event.stopPropagation()
    },

    /**
     * Распределяет нажатие по областям компонента.
     * Если isDisabled, обработка прекращается и отдается false.
     * Если нажатие пришлось на выпадающее меню, выполняется
     * обработка нажатия по меню. Если нажатие пришлось на корневой
     * элемент, выполняется обработка нажатия по полю. Иначе, если
     * поле в фокусе или список раскрыт, выполняется обработка нажатия
     * вне компонента; нажатие вне поля без фокуса и без раскрытого
     * списка не обрабатывается.
     *
     * @param {MouseEvent} event Событие нажатия.
     * @returns {boolean|undefined} false при заблокированном поле,
     * в остальных случаях undefined.
     */
    _clickHandler(event) {
      if (this.isDisabled) {
        return false
      }

      const { target } = event

      if (this.menu && this.menu.$el && isChildOf(target, this.menu.$el)) {
        this._clickOnMenu(event)
      } else if (this.$el && isChildOf(target, this.$el)) {
        this._clickOnMain(event)
      } else if (this.isFocused || this._data._showDropdown) {
        this._clickOutside(event)
      }
    },
    /**
     * Обрабатывает нажатие по полю.
     * Поле помечается как получившее фокус, список раскрывается,
     * если был закрыт, и фокус переводится на поле ввода. Если
     * combobox и clearOnFocus, строка поиска очищается. Текущая
     * ошибка и признак выполненной проверки обязательного выбора
     * сбрасываются.
     */
    _clickOnMain() {
      this._data._focused = true

      if (!this._data._showDropdown) {
        this._data._showDropdown = true
      }

      this._focus()

      if (this.combobox && this.clearOnFocus) {
        this._clearSearchValue()
      }

      this._clearCurrentError()
    },
    /**
     * Обрабатывает нажатие по выпадающему меню.
     * Если нет multiple, фокус снимается и список закрывается.
     * Строка поиска очищается, фокус переводится на поле ввода.
     */
    _clickOnMenu() {
      if (!this.multiple) {
        this._data._focused = false
        this._data._showDropdown = false
      }

      this._clearSearchValue()
      this._focus()
    },
    /**
     * Обрабатывает нажатие вне компонента.
     * Фокус снимается, список закрывается, курсор клавиатуры
     * сбрасывается, фокус снимается с поля ввода. Если есть строка
     * поиска и isFreeInput и setOnBlur и нет fromItem, строка поиска
     * добавляется к выбранным значениям. Строка поиска очищается,
     * после чего проверяется обязательный выбор.
     *
     * @param {MouseEvent} [event] Событие нажатия.
     * @param {boolean} [fromItem] Признак вызова после выбора пункта
     * списка.
     */
    _clickOutside(event, fromItem) {
      this._data._focused = false
      this._data._showDropdown = false
      this._data._cursorPosition = -1

      this._blur()

      if (this.search && this.isFreeInput && this.setOnBlur && !fromItem) {
        this._setValue(this.search)
      }

      this._clearSearchValue()
      this._setErrorOnRequired()
    },

    /**
     * Обрабатывает нажатие по пункту списка.
     * Действие нажатия по умолчанию и его распространение
     * отменяются. Если строка поиска пуста, фокус снимается. Если
     * пункт не заблокирован, его значение передается в выбранные
     * значения, а при отсутствии multiple выполняется обработка
     * нажатия вне компонента.
     *
     * @param {MouseEvent} event Событие нажатия.
     * @param {object} item Пункт списка.
     */
    _itemClickHandler(event, item) {
      this._preventClick(event)

      if (isEmpty(this.search)) {
        this._data._focused = false
      }

      if (!item._disabled) {
        this.proxy = [ get(item, this.$_itemValue, item) ]

        if (!this.multiple) {
          this._clickOutside(event, true)
        }
      }
    },

    /**
     * Переводит фокус на поле ввода после перерисовки.
     * Если поля ввода нет, ничего не происходит.
     */
    _focus() {
      this.$nextTick(() => {
        if (this.input && this.input.focus) {
          this.input.focus({ preventScroll: true })
        }
      })
    },
    /**
     * Снимает фокус с поля ввода после перерисовки.
     * Если поля ввода нет, ничего не происходит.
     */
    _blur() {
      this.$nextTick(() => {
        if (this.input && this.input.blur) {
          this.input.blur()
        }
      })
    }
  }
}
